<script setup>
import { useAxios } from '@/hooks/useAxios';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    justifyContent: {
        type: String,
        default: 'center'
    },
    width: {
        type: String,
        default: '9rem'
    },
    height: {
        type: String,
        default: '2.3rem'
    },
    vehicleGroupId: {
        type: Number,
        default: 0
    },
    rentType: {
        type: String,
        default: ''
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const insuranceData = ref(null);
const isReadonly = computed(() => props.readonly);
const isVisible = computed(() => props.visible);
const inputValue = ref(props.modelValue);
const ValidationMsg = ref('');
const errMessage = ref(props.errmsg);
const isPopupVisible = ref(false);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
const showAlert = ref(false);
let alertTimeout = null;

const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};
const searchQuery = ref('');
const filteredOptions = ref([]);
const rentTypeReadOnly = computed(() => props.rentType === 'SHORT' || props.rentType === 'CRSH');

const filterOptions = () => {
    if (insuranceData.value === null) return;
    filteredOptions.value = insuranceData.value.filter((option) => option.text.toLowerCase().includes(searchQuery.value.toLowerCase()));
};

onMounted(async () => {
    // await Promise.all([searchInsurance()]);
    // nextTick(() => {
    //     filteredOptions.value = insuranceData.value;
    // });
});

const searchInsurance = async () => {
    let url = '';
    //console.log(props.vehicleGroupId);
    if (props.vehicleGroupId !== null && props.vehicleGroupId !== 0) url = `/api/insurance/col?vehicleGroupId=${props.vehicleGroupId}`;
    else url = `/api/insurance/col`;
    const config = {
        method: 'get'
        // params: {
        //     vehicleGroupId: props.vehicleGroupId
        // }
    };
    try {
        const { execute, setHeader } = useAxios(url, config, {
            immediate: false,
            onSuccess(response) {
                const newList = response.data.data.map((item) => ({
                    value: item.collision_insurance_state,
                    text: item.insurance_name,
                    id: item.collision_insurance_state,
                    insurance_name: item.insurance_name,
                    collision_coverage_type: item.collision_coverage_type,
                    collision_insurance_age: item.collision_insurance_age,
                    collision_insurance_excess: item.collision_insurance_excess, // 자차면책금
                    collision_insurance_state: item.collision_insurance_state, //면책종류
                    collision_insurance_coverage: item.collision_insurance_coverage, // 면책금액
                    is_registered: rentTypeReadOnly ? item.is_registered : 'Y' // 자차 요금표 등록 여부
                }));
                insuranceData.value = newList;
            },
            onError(err) {
                console.error('Error:', err.message);
                insuranceData.value = [];
            }
        });
        await execute();
    } catch (err) {
        console.error(err);
        insuranceData.value = [];
    }
};

watch(
    () => props.modelValue,
    (newVal) => {
        if (props.upper) {
            newVal = newVal.toUpperCase();
        }

        inputValue.value = newVal || '';
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const togglePopup = async () => {
    await Promise.all([searchInsurance()]);
    nextTick(() => {
        isPopupVisible.value = !isPopupVisible.value;
        filteredOptions.value = insuranceData.value;
    });
};

const closePopup = () => {
    isPopupVisible.value = false;
};

const selectValue = (option) => {
    // if (option.is_registered === 'N') return;

    if (option.is_registered === 'N' && rentTypeReadOnly.value) {
        showAlert.value = true;
        clearTimeout(alertTimeout);
        alertTimeout = setTimeout(() => (showAlert.value = false), 2000);
        return;
    }

    emit('update:modelValue', option.insurance_name);
    emit('input', {
        id: props.inputId,
        value: option.value,
        insurance_name: option.text || '',
        collision_cost: option.collision_cost || '',
        collision_coverage_type: option.collision_coverage_type || '',
        collision_insurance_age: option.collision_insurance_age || '',
        collision_insurance_coverage: option.collision_insurance_coverage || '',
        collision_insurance_state: option.collision_insurance_state || '',
        is_registered: option.is_registered || ''
    });
    ValidationMsg.value = '';
    closePopup();
};

watch(isPopupVisible, (newValue) => {
    if (newValue) {
        searchQuery.value = '';
        filterOptions();
    }
});

const numberFormatter = (num) => {
    let value = num;
    if (typeof value === 'number') {
        value = value.toLocaleString(); // 숫자에 천 단위 콤마 추가
    } else if (typeof value === 'string' && !isNaN(value)) {
        value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ','); // 문자열로 숫자일 경우 정규식으로 콤마 추가
    } else {
        value = value; // 숫자가 아니면 원래 값 그대로
    }
    return value;
};

const getStateCommonCode = (val) => {
    let options = commonCode('COLLISION_COVERAGE_TYPE').value;
    let value = '';
    options && options.length > 0
        ? options.map((c, i) => {
              if (c.value == val) {
                  value = c.text;
              }
          })
        : '';
    return value;
};
</script>

<template>
    <!-- 불러오기는 Readonly값일경우에는 표출 하지않도록 한다. -->
    <div
        v-if="!isReadonly && isVisible"
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>

        <div class="relative flex items-center w-full h-12" :style="{ justifyContent: justifyContent || 'center' }">
            <button @click="togglePopup" class="flex-1 px-4 h-[2.3rem] text-[0.875rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold">
                자차보험 선택
            </button>
        </div>

        <Teleport to="body">
            <div v-if="isPopupVisible" class="popup-overlay">
                <div class="popup-content">
                    <div v-if="showAlert" class="alert-box mt-4">요금표 등록 후 선택할 수 있습니다.</div>
                    <!-- <h3>선택</h3> -->
                    <!-- <input v-model="searchQuery" @input="filterOptions" placeholder="자차보험을 검색하세요" class="search-input" style="margin-top: 0.5rem" /> -->
                    <div class="options-container">
                        <table class="colinsurance-grid">
                            <thead>
                                <tr>
                                    <th>자차보험</th>
                                    <th>보험가입연령</th>
                                    <th>자차한도</th>
                                    <th v-if="rentTypeReadOnly">자차요금표</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="option in filteredOptions" :key="option.value" @click="selectValue(option)" class="popup-item">
                                    <td style="text-align: left; padding-left: 1rem">{{ option.insurance_name }}</td>
                                    <td>만 {{ option.collision_insurance_age }}세</td>
                                    <td>
                                        {{ numberFormatter(option.collision_insurance_coverage) }}
                                        {{ option.collision_coverage_type === 'R' ? '%' : '원' }}
                                    </td>
                                    <td v-if="rentTypeReadOnly" :style="{ color: option.is_registered === 'Y' ? 'inherit' : 'red' }">
                                        {{ option.is_registered === 'Y' ? '등록' : '미등록' }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <button @click="closePopup" class="close-button">닫기</button>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.colinsurance-grid th {
    position: sticky;
    top: 0;
    background-color: #f2f2f2;
    z-index: 10;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus {
    border-color: #0038ea;
    outline: none;
}

.error-border {
    border: 1px solid red !important;
}
.custom-input.p-inputtext {
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
}

.custom-input.p-inputtext:focus {
    border-color: #0038ea !important;
}

.alert-box {
    padding: 0.5rem 1rem;
    background: red;
    color: white;
    border-radius: 0.25rem;
    margin-bottom: 0.5rem;
    text-align: center;
}
.popup-item.disabled {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 0.9rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

.popup-item {
    margin: 0.5rem 0;
    font-family: 'SCoreDream400';
    font-size: 0.95rem;
    cursor: pointer;
}

.popup-item:hover {
    color: #0038ea;
}

.close-button {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    margin-top: 1rem;
    background: #f1f1f1;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
}

.close-button:hover {
    background: #ddd;
}

.popup-overlay {
    user-select: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}
.popup-content {
    background: #fff;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 1rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    width: auto;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    text-align: left;
    min-width: 30rem;
}

.search-input {
    width: 100%;
    padding: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
}

.options-container {
    flex-grow: 1;
    height: 21rem;
    max-height: 30rem;
    overflow-y: auto;
}

.popup-item {
    padding: 0.4rem;
    cursor: pointer;
    transition: background-color 0.2s;
}

.popup-item:hover {
    background-color: #f0f0f0;
}

.options-container::-webkit-scrollbar {
    width: 0.5rem;
}

.options-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb {
    background: #c9c6c6;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb:hover {
    background: #727272;
}

.colinsurance-grid {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
}

.colinsurance-grid th,
.colinsurance-grid td {
    border: 0.1rem solid #ddd;
    padding: 0.3rem;
    font-family: 'SCoreDream300';
    font-size: 0.875rem;
}

.colinsurance-grid th {
    background-color: #f2f2f2;
    text-align: center;
}

.colinsurance-grid td {
    text-align: left;
    padding: 0.3rem 1rem;
}

.colinsurance-grid tr:nth-child(even) {
    background-color: #f9f9f9;
}

.colinsurance-grid th:hover {
    cursor: default;
}

.colinsurance-grid tr:hover {
    background-color: #f5f5f5;
    cursor: pointer;
}
</style>
